import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarClock } from 'lucide-react';
import { format } from 'date-fns';
import { parseStoredDateTime } from '@/utils/timeUtils';

interface Booking {
  id: number;
  service_id?: number | null;
  start_time?: string | null;
  end_time?: string | null;
  status?: string | null;
  services?: {
    id: number;
    name: string;
  } | null;
  customer_id?: string | null;
  note?: string | null;
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  customer_address?: string | null;
}

interface BookingCardProps {
  booking: Booking;
  onClick?: () => void;
  className?: string;
}

export const BookingCard: React.FC<BookingCardProps> = ({ 
  booking, 
  onClick,
  className = ""
}) => {
  const getStatusLabel = (status: string | null): string => {
    if (!status) return 'New';
    
    switch (status.toLowerCase()) {
      case 'new': return 'New';
      case 'accepted': return 'Accepted';
      case 'declined': return 'Declined';
      case 'cancelled': return 'Cancelled';
      case 'completed': return 'Completed';
      case 'blocked': return 'Blocked';
      case 'travel': return 'Travel';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getStatusColor = (status: string | null): string => {
    if (!status) return 'text-amber-600';
    
    switch (status.toLowerCase()) {
      case 'new': return 'text-amber-600';
      case 'accepted': return 'text-green-600';
      case 'declined': 
      case 'cancelled': return 'text-red-600';
      case 'completed': return 'text-blue-600';
      case 'blocked': 
      case 'travel': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <Card 
      key={booking.id} 
      className={`overflow-hidden ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} ${className}`}
      onClick={onClick}
    >
      <CardHeader className="pb-3">
        <CardTitle>{booking.services?.name || "Service"}</CardTitle>
        <CardDescription>
          {booking.start_time && format(parseStoredDateTime(booking.start_time), "MMMM d, yyyy")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center text-sm gap-2">
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              {booking.start_time && format(parseStoredDateTime(booking.start_time), "h:mm a")} - 
              {booking.end_time && format(parseStoredDateTime(booking.end_time), "h:mm a")}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Status:</span>
            <span className={`text-sm font-medium ${getStatusColor(booking.status)}`}>
              {getStatusLabel(booking.status)}
            </span>
          </div>

          {booking.note && (
            <div className="text-sm text-muted-foreground">
              <span className="font-medium">Note:</span> {booking.note}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};